import React, { createContext, useReducer, useContext, ReactNode, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Route from 'src/config/routes'
import { AppMode } from '@customTypes/Generic'
import { TOTAL_STEPS } from 'src/config/constants'
import { useIsCityVariant } from '@hooks/useIsCityVariant'
import { useIsAllClinicsVariant } from '@hooks/useIsAllClinicsVariant'

type Action = { type: 'setStep' | 'setTotalSteps'; payload: number }
type State = {
  step?: number
  totalSteps: number
}
type Dispatch = (action: Action) => void
type StepsProviderProps = { children: ReactNode; mode?: AppMode }

const StepsContext = createContext<State | undefined>(undefined)
const StepsDispatchContext = createContext<Dispatch | undefined>(undefined)

const defaultState = {
  step: 1,
  totalSteps: TOTAL_STEPS.REGULAR,
}

const stepsReducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'setStep':
      return { ...state, step: action.payload }
    case 'setTotalSteps':
      return { ...state, totalSteps: action.payload }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

const getRouteToStep = (isCityVariant: boolean, isAllClinicsVariant: boolean): Partial<Record<Route, number>> => {
  const getStep = (stepNumber: number) => {
    let newStepNumber = stepNumber
    if (isCityVariant || isAllClinicsVariant) newStepNumber++
    // TODO: This should be written in a cleaner way
    newStepNumber--
    return newStepNumber
  }

  return {
    [Route.home]: 1,
    [Route.upsell]: 1,
    [Route.painDetails]: 1,
    [Route.treatment]: 1,
    [Route.district]: 2,
    [Route.clinic]: 2,

    [Route.patient]: isCityVariant || isAllClinicsVariant ? 3 : 2,
    [Route.practitioner]: getStep(4),
    [Route.appointment]: getStep(4),
    [Route.contact]: getStep(5),
    [Route.phoneVerification]: getStep(5),
    [Route.success]: getStep(5),

    [Route.oldPatientPhoneVerification]: getStep(3),
    [Route.oldPatientSelectAccount]: getStep(3),
  }
}

export const StepsProvider: React.FC<StepsProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(stepsReducer, { ...defaultState, totalSteps: TOTAL_STEPS.REGULAR })

  return (
    <StepsContext.Provider value={state}>
      <StepsDispatchContext.Provider value={dispatch}>
        <CurrentStepSetter>{children}</CurrentStepSetter>
      </StepsDispatchContext.Provider>
    </StepsContext.Provider>
  )
}

const CurrentStepSetter = ({ children }: { children: React.ReactNode }) => {
  const isCityVariant = useIsCityVariant()
  const isAllClinicsVariant = useIsAllClinicsVariant()
  const { pathname } = useLocation()
  const stepsDispatch = useStepsDispatch()
  const stepsState = useStepsState()
  const totalSteps = (() => {
    if (isCityVariant) {
      return TOTAL_STEPS.CITY - 1
    }

    if (isAllClinicsVariant) {
      return TOTAL_STEPS.ALL_CLINICS - 1
    }

    return TOTAL_STEPS.SHORTER_FLOW
  })()
  const currentStep = getRouteToStep(isCityVariant, isAllClinicsVariant)[pathname as Route]

  useEffect(() => {
    stepsDispatch({ type: 'setStep', payload: currentStep || 0 })
  }, [currentStep, stepsDispatch])

  useEffect(() => {
    if (stepsState.totalSteps !== totalSteps) {
      stepsDispatch({ type: 'setTotalSteps', payload: totalSteps })
    }
  }, [stepsDispatch, stepsState.totalSteps, totalSteps])

  return <>{children}</>
}

export const useStepsState = () => {
  const context = useContext(StepsContext)
  if (context === undefined) {
    throw new Error('useStepsState must be user within a StepsProvider')
  }
  return context
}

export const useStepsDispatch = () => {
  const context = useContext(StepsDispatchContext)
  if (context === undefined) {
    throw new Error('useStepsDispatch must be used within a StepsProvider')
  }
  return context
}
